/**
 * 宠物学习的查询：读写口从哪来、查询键、开没开、能不能照顾这只宠物。
 * - 读写口：外面用 TeachApiProvider 换一个（测试用）；没有就按数据模式——live 用全站唯一的 HTTP 客户端；演示模式没有，入口不显示、页面说暂时看不了，不编教学记录。
 *   照世界页 usePublicMapReader 的写法；shared/services 开放 teach 以后可以改成从 useServices() 取（6c2b 117-3 正在改 types.ts，先不加键）。
 * - 开没开：能力表 pets.teach（adm1：开关 web_teach_enabled 默认关；关着时读口照常、写口 409）。入口只在开着时出现。
 * - 能不能照顾：这个家的详情里 your_permissions 含 care（adm1 的路由同样按 Action.care 核）；和家庭页共用同一个查询键。
 * - 准备中的单每 20 秒只读刷新一次（不写倒计时，不替主人重发）。
 * - 基础动作（useBasicMotions）：有在学的段时每 60 秒只读重拉一次角色状态，页面藏到后台停拉。
 * - 服务只在 queryFn 里取（不在渲染时解构 useServices()）：入口挂在「我的」页上，别的页面用例用严格替身，没给的服务一碰就抛；
 *   在 queryFn 里取，没给就是这条查询失败、入口不出，「我的」页其余部分照旧。
 */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { liveTeachApi, type TeachApi } from "./teach.service";
import type { HomeSnapshot } from "@/shared/contracts";
import { readBasicMotions, type BasicMotions } from "./basicMotions";
import type { TeachOrder, TeachState } from "./teachTypes";

const ApiContext = createContext<TeachApi | null>(null);

export function TeachApiProvider({ api, children }: { api: TeachApi; children: ReactNode }) {
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

let live: TeachApi | null = null;

export function useTeachApi(): TeachApi | null {
  const injected = useContext(ApiContext);
  if (injected) return injected;
  if (env.dataMode !== "live") return null;
  return (live ??= liveTeachApi(apiClient));
}

/** 查询键：模块自己的（shared/query 的 queryKeys 要报批才加），按账号分。 */
export const teachKeys = {
  all: ["teach"] as const,
  entitlement: (userId: string) => ["teach", "entitlement", userId] as const,
  orders: (userId: string, petId: string) => ["teach", "orders", userId, petId] as const,
  order: (userId: string, orderId: string) => ["teach", "order", userId, orderId] as const,
  motion: (userId: string, petId: string) => ["teach", "motion", userId, petId] as const,
};

export const TEACH_CAPABILITY = "pets.teach";

/** 能力表里 pets.teach 开着：true；关着或没登记：false；还在读、读失败：null（入口先不出）。 */
export function useTeachOpen(): boolean | null {
  const services = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => services.platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  if (env.dataMode !== "live") return false;
  if (!meta.data) return null;
  return meta.data.capabilities.find((c) => c.key === TEACH_CAPABILITY)?.status === "available";
}

/** 当前这个家里，我能不能照顾宠物（your_permissions 含 care）。读不到：null。 */
export function useCanCare(): boolean | null {
  const services = useServices();
  const ctx = useOptionalCurrentHousehold();
  const userId = ctx?.userId ?? null;
  const householdId = ctx?.household?.household_id ?? null;
  const detail = useQuery({
    queryKey: queryKeys.householdDetail(userId ?? "-", householdId ?? "-"),
    queryFn: ({ signal }) => services.households.detail(householdId!, signal),
    enabled: env.dataMode === "live" && Boolean(userId && householdId),
  });
  if (!userId || !householdId || !detail.data) return null;
  return detail.data.your_permissions.includes("care");
}

const IN_PROGRESS: ReadonlySet<TeachState> = new Set(["queued", "preparing", "checking", "result_pending", "repreparing"]);
export const TEACH_POLL_MS = 20_000;

export function isInProgress(state: TeachState): boolean {
  return IN_PROGRESS.has(state);
}

export function useEntitlement() {
  const api = useTeachApi();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return useQuery({
    queryKey: teachKeys.entitlement(userId ?? "-"),
    queryFn: ({ signal }) => api!.entitlement(signal),
    enabled: Boolean(api && userId),
    staleTime: 15_000,
    retry: false,
  });
}

export function useOrdersOfPet(petId: string | null) {
  const api = useTeachApi();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return useQuery({
    queryKey: teachKeys.orders(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => api!.ordersOfPet(petId!, signal),
    enabled: Boolean(api && userId && petId),
    retry: false,
    refetchInterval: (query) => (query.state.data?.items.some((o) => isInProgress(o.state)) ? TEACH_POLL_MS : false),
  });
}

export function useOrder(orderId: string | null) {
  const api = useTeachApi();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return useQuery<TeachOrder>({
    queryKey: teachKeys.order(userId ?? "-", orderId ?? "-"),
    queryFn: ({ signal }) => api!.order(orderId!, signal),
    enabled: Boolean(api && userId && orderId),
    retry: false,
    refetchInterval: (query) => (query.state.data && isInProgress(query.state.data.state) ? TEACH_POLL_MS : false),
  });
}

/** 学会的动作的片段：pets 服务现成的 motion 读口（purpose=home）。只在单的状态能预览时才读（./teachModel canPreview）。 */
export function useTaughtMotion(petId: string | null, enabled: boolean) {
  const services = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return useQuery({
    queryKey: teachKeys.motion(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => (services.pets.motion ? services.pets.motion(petId!, signal) : Promise.resolve(null)),
    enabled: enabled && env.dataMode === "live" && Boolean(userId && petId),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * 小窝此刻的样子，给「TA 的小动作」顶上的场景用（TEACH-PAGES-VISUAL-117）：天色、TA 在不在家、睡没睡。
 * 和小窝同一个读口（world.home）、同一个查询键（queryKeys.homeFor），小窝开着时直接用它那份。
 * - 天色只认后端作息（life.rhythm.sky，同 homeStage 的 skyOf），读不到当白天，不按本机时钟猜；
 * - 在不在家：有 life 看 activity.kind（away 就是出门了），没有 life 看 presence（同 HomeScene 画不画 TA 的判断）；
 * - 读不到小窝：settled 照样为 true，当在家、白天、醒着画（场景只是 TA 的屋子，不拿它说事）。
 */
export function useHomeGlance(petId: string | null): { settled: boolean; night: boolean; away: boolean; asleep: boolean } {
  const services = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const home = useQuery<HomeSnapshot>({
    queryKey: queryKeys.homeFor(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => services.world.home(petId!, signal),
    enabled: env.dataMode === "live" && Boolean(userId && petId),
    staleTime: 30_000,
    retry: false,
  });
  if (!home.data) return { settled: !home.isPending, night: false, away: false, asleep: false };
  const snapshot = home.data;
  const life = (snapshot.pets ?? snapshot.household?.pets)?.find((p) => p.pet_id === petId)?.life ?? null;
  const away = life ? life.activity.kind === "away" : snapshot.presence !== "at_home";
  return { settled: true, night: life?.rhythm?.sky === "night", away, asleep: life?.activity.kind === "sleeping" };
}

export const BASIC_MOTIONS_POLL_MS = 60_000;
const CHARACTER_CAPABILITY = "character.state";

/** 重拉间隔：还有在学的段才 60 秒拉一次，没有就不拉 */
export function basicMotionsPollInterval(state: unknown): number | false {
  return readBasicMotions(state)?.clips.some((clip) => clip.status === "preparing") ? BASIC_MOTIONS_POLL_MS : false;
}

/**
 * 基础动作学到哪了（UI2-BASIC-MOTION-ROWS-117）：读角色状态里的 basic_motions（A-2 BASIC-PACK-117）。
 * 和小窝同一个读口（pets.character）、同一个查询键（queryKeys.characterFor），小窝的 usePetCharacter 不改；
 * 能力表 character.state 没开就不读（同小窝）。
 * - 有在学的段（status＝preparing）时这一页开着每 60 秒只读重拉一次；页面藏到后台不拉（React Query 默认 refetchIntervalInBackground＝false）。
 *   都会了或都暂时做不好就不再拉。不发任何写请求。
 * - 没有这一栏、null、形状不对、第一次就读失败：回 null，整块不出，不转圈、不编数；读到过以后重拉失败，照旧给上一次读到的。
 */
export function useBasicMotions(petId: string | null): BasicMotions | null {
  const services = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => services.platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  const available = meta.data?.capabilities.find((c) => c.key === CHARACTER_CAPABILITY)?.status === "available";
  const state = useQuery({
    queryKey: queryKeys.characterFor(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => services.pets.character(petId!, signal),
    enabled: env.dataMode === "live" && available && Boolean(petId),
    staleTime: 15_000,
    retry: false,
    refetchInterval: (query) => basicMotionsPollInterval(query.state.data),
  });
  return readBasicMotions(state.data);
}
